import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../../app/store/utils/baseQueryWithReauth';
import { unwrapApiData } from './unwrapApiData';
import type {
  CreateLegalDocumentInput,
  CreateLegalVersionInput,
  LegalDocument,
  LegalDocumentCatalogItem,
  LegalDocumentDetail,
  LegalDocumentVersion,
} from '../../application/types';

export const SYSTEM_LEGAL_BASE = '/system/owner/legal';

export const legalApi = createApi({
  reducerPath: 'legalApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['LegalDocument', 'LegalDocumentList'],
  endpoints: build => ({
    getLegalDocuments: build.query<LegalDocumentCatalogItem[], void>({
      query: () => ({
        url: `${SYSTEM_LEGAL_BASE}/documents`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<LegalDocumentCatalogItem[]>(response),
      providesTags: ['LegalDocumentList'],
    }),

    getLegalDocument: build.query<LegalDocumentDetail, number>({
      query: id => ({
        url: `${SYSTEM_LEGAL_BASE}/documents/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<LegalDocumentDetail>(response),
      providesTags: (_result, _error, id) => [{ type: 'LegalDocument', id }],
    }),

    createLegalDocument: build.mutation<LegalDocument, CreateLegalDocumentInput>({
      query: body => ({
        url: `${SYSTEM_LEGAL_BASE}/documents`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<LegalDocument>(response),
      invalidatesTags: ['LegalDocumentList'],
    }),

    createLegalVersion: build.mutation<
      LegalDocumentVersion,
      { documentId: number; body: CreateLegalVersionInput }
    >({
      query: ({ documentId, body }) => ({
        url: `${SYSTEM_LEGAL_BASE}/documents/${documentId}/versions`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<LegalDocumentVersion>(response),
      invalidatesTags: (_result, _error, { documentId }) => [
        'LegalDocumentList',
        { type: 'LegalDocument', id: documentId },
      ],
    }),

    publishLegalVersion: build.mutation<
      LegalDocumentVersion,
      { documentId: number; versionId: number }
    >({
      query: ({ versionId }) => ({
        url: `${SYSTEM_LEGAL_BASE}/versions/${versionId}/publish`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<LegalDocumentVersion>(response),
      invalidatesTags: (_result, _error, { documentId }) => [
        'LegalDocumentList',
        { type: 'LegalDocument', id: documentId },
      ],
    }),
  }),
});

export const {
  useGetLegalDocumentsQuery,
  useGetLegalDocumentQuery,
  useCreateLegalDocumentMutation,
  useCreateLegalVersionMutation,
  usePublishLegalVersionMutation,
} = legalApi;
